import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Truck } from "lucide-react";
import { LINKS } from "@/config/links";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/logbook", label: "Logbook" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#040914]/80 border-b border-white/5"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-16 sm:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-2.5 group"
        >
          <span className="h-9 w-9 rounded-xl bg-[#00274C] border border-white/10 grid place-items-center group-hover:border-[#FFCB05]/50 transition-colors">
            <Truck className="h-4.5 w-4.5 text-[#FFCB05]" strokeWidth={2.25} />
          </span>
          <span className="font-display text-lg sm:text-xl font-semibold tracking-tight text-white">
            Linehaul <span className="text-[#FFCB05]">Books</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  isActive
                    ? "text-white bg-white/5"
                    : "text-[#9CA3AF] hover:text-white hover:bg-white/5"
                }`
              }
              end={item.to === "/"}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/portal"
            data-testid="nav-client-portal-btn"
            className="px-4 py-2 rounded-full text-sm font-medium text-white border border-white/10 hover:border-[#FFCB05]/40 hover:bg-white/5 transition-colors"
          >
            Client Portal
          </Link>
          <a
            href={LINKS.discoveryCall}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-book-call-btn"
            className="px-4 py-2 rounded-full text-sm font-semibold bg-[#FFCB05] text-[#040914] hover:bg-[#FFD633] transition-colors"
          >
            See if Linehaul is a fit
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          aria-label="Open menu"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden h-10 w-10 grid place-items-center rounded-lg border border-white/10 text-white"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div data-testid="nav-mobile-menu" className="md:hidden border-t border-white/5 bg-[#040914]/95 backdrop-blur-xl">
          <div className="px-6 py-4 space-y-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-mobile-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-lg text-sm ${
                    isActive ? "bg-white/5 text-white" : "text-[#9CA3AF] hover:text-white hover:bg-white/5"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="pt-3 grid grid-cols-2 gap-2">
              <Link
                to="/portal"
                data-testid="nav-mobile-client-portal-btn"
                className="text-center px-4 py-3 rounded-lg text-sm font-medium border border-white/10 text-white"
              >
                Client Portal
              </Link>
              <a
                href={LINKS.discoveryCall}
                target="_blank"
                rel="noreferrer"
                data-testid="nav-mobile-book-call-btn"
                className="text-center px-4 py-3 rounded-lg text-sm font-semibold bg-[#FFCB05] text-[#040914]"
              >
                See if Linehaul is a fit
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
